import { useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Cable, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { ContractHeader } from '@/components/contract/contract-header';
import { ApiError } from '@/lib/api/client';
import {
  usePmConnectionsQuery,
  useCreatePmConnectionMutation,
  useDeletePmConnectionMutation,
  useTestPmConnectionMutation,
  useRunPmSyncMutation,
  usePmSyncLogsQuery,
  type PmConnection,
  type CreatePmConnectionBody,
} from '@/lib/api/pm-integrations';
import { formatDateTime, titleCase } from '@/lib/utils/format';

/**
 * Contract → Integrations (ported PM-tool sync). One-way pull from
 * Jira / Smartsheet / Azure DevOps into the EDL. Synced items carry
 * external ids/links; IV&V review verdicts are locally authoritative —
 * a sync never overwrites them.
 */
export const Route = createFileRoute('/engagements/$engagementId/contract/integrations')({
  component: IntegrationsTab,
});

const PLATFORM_HINTS: Record<CreatePmConnectionBody['platform'], { base: string; scope: string }> =
  {
    jira: {
      base: 'https://yourcompany.atlassian.net',
      scope: 'JQL, e.g. project = ERP ORDER BY duedate',
    },
    smartsheet: { base: 'https://api.smartsheet.com', scope: 'Sheet ID (numeric)' },
    azure_devops: { base: 'https://dev.azure.com/yourorg/yourproject', scope: 'WIQL (optional)' },
  };

const inputCls =
  'mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300';

function IntegrationsTab() {
  const { engagementId } = useParams({
    from: '/engagements/$engagementId/contract/integrations',
  });
  const connections = usePmConnectionsQuery(engagementId);
  const [adding, setAdding] = useState(false);

  const items = connections.data?.items ?? [];

  return (
    <div className="space-y-6">
      <ContractHeader
        engagementId={engagementId}
        active="integrations"
        meta={
          <span>
            {items.length} connection{items.length === 1 ? '' : 's'} · one-way pull into the EDL ·
            IV&V verdicts are never overwritten by a sync
          </span>
        }
        actions={
          <Button
            type="button"
            size="sm"
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
            onClick={() => setAdding(true)}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New connection
          </Button>
        }
      />

      {adding ? (
        <NewConnectionCard engagementId={engagementId} onDone={() => setAdding(false)} />
      ) : null}

      {connections.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading connections…
        </div>
      ) : connections.error ? (
        <ErrorMessage error={connections.error} />
      ) : items.length === 0 && !adding ? (
        <EmptyState
          title="No PM-tool connections"
          description="Connect Jira, Smartsheet, or Azure DevOps to pull the vendor's plan into the Expected Deliverable List. Synced items keep a live link back to the source tool."
        />
      ) : (
        <ul className="space-y-4" role="list">
          {items.map((c) => (
            <ConnectionCard key={c.id} engagementId={engagementId} connection={c} />
          ))}
        </ul>
      )}
    </div>
  );
}

function NewConnectionCard({ engagementId, onDone }: { engagementId: string; onDone: () => void }) {
  const [platform, setPlatform] = useState<CreatePmConnectionBody['platform']>('jira');
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [scopeQuery, setScopeQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreatePmConnectionMutation(engagementId);

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <h2 className="text-h3 font-semibold text-ink-900">New PM-tool connection</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-meta font-medium text-ink-700">
            Platform
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as typeof platform)}
              className={inputCls}
            >
              <option value="jira">Jira</option>
              <option value="smartsheet">Smartsheet</option>
              <option value="azure_devops">Azure DevOps</option>
            </select>
          </label>
          <label className="block text-meta font-medium text-ink-700">
            Display name
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Vendor Jira — ERP program"
              className={inputCls}
            />
          </label>
        </div>
        <label className="block text-meta font-medium text-ink-700">
          Base URL (https)
          <input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder={PLATFORM_HINTS[platform].base}
            className={inputCls}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          {platform === 'jira' ? (
            <label className="block text-meta font-medium text-ink-700">
              Account email (Jira basic auth)
              <input
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                className={inputCls}
              />
            </label>
          ) : null}
          <label className="block text-meta font-medium text-ink-700">
            API token / PAT
            <input
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              className={inputCls}
            />
            <span className="mt-1 block text-ink-500">
              Stored server-side; never shown again after saving.
            </span>
          </label>
        </div>
        <label className="block text-meta font-medium text-ink-700">
          Scope — {PLATFORM_HINTS[platform].scope}
          <input
            value={scopeQuery}
            onChange={(e) => setScopeQuery(e.target.value)}
            className={inputCls}
          />
        </label>
        {error ? <p className="text-meta font-medium text-cite-broken">{error}</p> : null}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={
              !displayName.trim() || !baseUrl.trim() || !apiToken.trim() || create.isPending
            }
            onClick={() => {
              setError(null);
              create.mutate(
                {
                  platform,
                  display_name: displayName.trim(),
                  base_url: baseUrl.trim(),
                  auth_email: authEmail.trim() || null,
                  api_token: apiToken.trim(),
                  scope_query: scopeQuery.trim() || null,
                },
                {
                  onSuccess: onDone,
                  onError: (e) =>
                    setError(e instanceof ApiError ? `${e.code}: ${e.message}` : 'Create failed.'),
                },
              );
            }}
          >
            {create.isPending ? <Spinner /> : <Cable className="h-3.5 w-3.5" aria-hidden="true" />}
            Save connection
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ConnectionCard({
  engagementId,
  connection,
}: {
  engagementId: string;
  connection: PmConnection;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const test = useTestPmConnectionMutation();
  const sync = useRunPmSyncMutation(engagementId);
  const del = useDeletePmConnectionMutation(engagementId);
  const logs = usePmSyncLogsQuery(connection.id);

  return (
    <li>
      <Card>
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-body font-semibold text-ink-900">
                {connection.display_name}
                <span className="ml-2 rounded-chip bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-700">
                  {titleCase(connection.platform)}
                </span>
              </p>
              <p className="truncate text-meta text-ink-500">
                {connection.base_url}
                {connection.last_sync_at
                  ? ` · last sync ${formatDateTime(connection.last_sync_at)} (${connection.last_sync_status})`
                  : ' · never synced'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={test.isPending}
                onClick={() =>
                  test.mutate(connection.id, {
                    onSuccess: (r) => setMessage(r.ok ? `✓ ${r.detail}` : `✗ ${r.detail}`),
                    onError: (e) =>
                      setMessage(e instanceof ApiError ? `✗ ${e.message}` : '✗ Test failed.'),
                  })
                }
              >
                {test.isPending ? <Spinner /> : null}
                Test
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={sync.isPending}
                onClick={() =>
                  sync.mutate(connection.id, {
                    onSuccess: (r) =>
                      setMessage(
                        `Sync ${r.status}: ${r.items_created} created, ${r.items_updated} updated, ${r.items_skipped} unchanged of ${r.items_seen} seen.${r.error ? ` ${r.error}` : ''}`,
                      ),
                    onError: (e) =>
                      setMessage(e instanceof ApiError ? `✗ ${e.message}` : '✗ Sync failed.'),
                  })
                }
              >
                {sync.isPending ? (
                  <Spinner />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Sync now
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={del.isPending}
                className={confirmDelete ? 'border-cite-broken text-cite-broken' : undefined}
                onClick={() => {
                  if (confirmDelete) del.mutate(connection.id);
                  else setConfirmDelete(true);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                {confirmDelete ? 'Confirm' : 'Remove'}
              </Button>
            </div>
          </div>

          {message ? (
            <p className="rounded-card border border-ink-100 bg-paper-50 px-3 py-2 text-meta text-ink-700">
              {message}
            </p>
          ) : null}

          {(logs.data?.items ?? []).length > 0 ? (
            <div>
              <p className="text-meta font-medium uppercase tracking-wide text-ink-500">
                Recent syncs
              </p>
              <ul className="mt-1 space-y-1">
                {(logs.data?.items ?? []).slice(0, 5).map((l) => (
                  <li
                    key={l.id}
                    className="flex flex-wrap justify-between gap-2 text-meta text-ink-700"
                  >
                    <span>
                      {formatDateTime(l.created_at)} · <strong>{l.status}</strong>
                      {l.error ? ` — ${l.error}` : ''}
                    </span>
                    <span className="font-mono">
                      +{l.items_created} / ~{l.items_updated} / ={l.items_skipped} ·{' '}
                      {(l.duration_ms / 1000).toFixed(1)}s
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </li>
  );
}
